import { useState, type ReactNode } from "react";
import { Card } from "../../components/ui/Card";
import { Logo } from "../../components/ui/Logo";
import { Equalizer, LiveBadge } from "../../components/viz/OnAir";
import { useNow } from "../../hooks/useNow";
import { formatClock } from "../../lib/uptime";

/** Example channels for the pitch panel, with how long each has been live when the page opens. */
const DEMO = [
  { name: "Rain sounds for sleep 24/7", where: "YouTube · 4K 60", upMs: (13 * 24 + 4) * 3_600_000 + 17 * 60_000 },
  { name: "Lofi beats radio", where: "Twitch · 1080p 60", upMs: (2 * 24 + 7) * 3_600_000 + 41 * 60_000 },
  { name: "Sunday service replay", where: "Facebook · 9:16", upMs: 5 * 3_600_000 + 8 * 60_000 },
];

/** The landing page's on-air board: example streams with clocks that tick every second. */
function DemoBoard() {
  const [opened] = useState(() => Date.now());
  const now = useNow(1_000);
  return (
    <div aria-hidden="true" className="flex flex-col overflow-hidden rounded-2xl border-2 border-board-rule bg-ink-2">
      <div className="flex items-center justify-between border-b-2 border-board-rule px-5 py-3">
        <span className="font-mono text-xs uppercase tracking-widest text-board-muted">on air now</span>
        <LiveBadge />
      </div>
      {DEMO.map((d) => (
        <div key={d.name} className="flex items-center justify-between gap-4 border-b border-board-rule px-5 py-3.5 last:border-b-0">
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-semibold text-white">{d.name}</span>
            <span className="font-mono text-xs text-board-muted">{d.where}</span>
          </div>
          <span className="shrink-0 font-mono text-sm text-white tabular-nums">{formatClock(d.upMs + now - opened)}</span>
        </div>
      ))}
      <div className="px-5 pt-1 pb-4"><Equalizer bars={64} className="h-6" /></div>
    </div>
  );
}

/** Split screen: the landing page's pitch and on-air board on ink, the form on a raised card. */
export function AuthLayout({ title, children, footer }: { title: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="grid min-h-dvh lg:grid-cols-2">
      <section className="hidden flex-col justify-between gap-12 bg-ink p-16 text-white lg:flex">
        <Logo inverted />
        <div className="flex flex-col gap-5 motion-safe:animate-rise">
          <h1 className="text-5xl leading-[1.05] font-bold tracking-tighter xl:text-6xl">
            <span className="block">Your channel stays live.</span>
            <span className="block text-board-muted">Your computer stays off.</span>
          </h1>
          <p className="max-w-md text-base text-line">
            Loop your videos to YouTube, Twitch, Facebook and any RTMP target, around the clock, from our servers.
          </p>
        </div>
        <div className="motion-safe:animate-rise [animation-delay:150ms]"><DemoBoard /></div>
      </section>

      <section className="flex flex-col items-center justify-center gap-8 px-4 py-12 sm:px-16">
        <div className="lg:hidden"><Logo /></div>
        <Card raised className="flex w-full max-w-md flex-col gap-4.5 p-8 motion-safe:animate-rise">
          <h2 className="text-3xl font-bold">{title}</h2>
          {children}
        </Card>
        {footer && <div className="flex w-full max-w-md flex-wrap justify-between gap-2">{footer}</div>}
      </section>
    </div>
  );
}
